import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, HardDrive, UserPlus, Loader2, Zap } from "lucide-react";

function Shell({ title, kicker, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl border themed-border shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] font-bold text-[#22D3EE]">{kicker}</div>
            <h2 className="font-display font-bold text-xl tracking-tighter mt-0.5">{title}</h2>
          </div>
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-neutral-400 hover:text-[#0A0C10]"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function DiskSpaceModal({ onClose, usedMb, quotaMb }) {
  const [packages, setPackages] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/subscriptions/purchase-options")
      .then(({ data }) => setPackages(data.storage_packages || []))
      .catch(() => setPackages([]));
  }, []);

  const buy = async (pkg) => {
    setBusy(pkg.id);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        kind: "storage", code: pkg.id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setBusy(null); }
  };

  return (
    <Shell title="Buy more Disk Space" kicker="/ disk space" onClose={onClose} testid="disk-space-modal">
      <div className="text-xs text-neutral-500 mb-4 inline-flex items-center gap-1.5">
        <HardDrive size={13} className="text-[#22D3EE]" />
        Current: <strong>{Math.round(usedMb || 0)} MB / {quotaMb || 500} MB</strong>
      </div>
      {packages === null ? (
        <div className="text-xs text-neutral-500 inline-flex items-center gap-2 py-4"><Loader2 size={12} className="animate-spin" /> Loading packages…</div>
      ) : (
        <div className="space-y-2">
          {packages.map((p) => (
            <button key={p.id} onClick={() => buy(p)} disabled={busy}
              data-testid={`storage-pkg-${p.id}`}
              className="w-full flex items-center gap-3 border border-neutral-200 hover:border-[#22D3EE] rounded-lg px-4 py-3 text-left transition-colors disabled:opacity-60">
              <Zap size={14} className="text-[#22D3EE] shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-bold">+{p.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">added to your quota instantly</div>
              </div>
              <div className="text-sm font-bold">{busy === p.id ? <Loader2 size={14} className="animate-spin" /> : `$${Number(p.price).toFixed(2)}`}</div>
            </button>
          ))}
        </div>
      )}
      <p className="text-[10px] text-neutral-400 mt-4">Secure payment via Stripe. Storage is added as soon as payment completes.</p>
    </Shell>
  );
}

export function SeatsModal({ onClose, current, limit }) {
  const [seatPrice, setSeatPrice] = useState(null);
  const [qty, setQty] = useState(5);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/subscriptions/purchase-options")
      .then(({ data }) => setSeatPrice(Number(data.seat_price || 1)))
      .catch(() => setSeatPrice(1));
  }, []);

  const buy = async () => {
    const n = Number(qty);
    if (!n || n < 1) return toast.error("Enter how many seats you need");
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        kind: "seats", quantity: n, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setBusy(false); }
  };

  return (
    <Shell title="Buy Student Seats" kicker="/ student seats" onClose={onClose} testid="seats-modal">
      <div className="text-xs text-neutral-500 mb-4 inline-flex items-center gap-1.5">
        <UserPlus size={13} className="text-[#22D3EE]" />
        Current: <strong>{current} / {limit} seats used</strong>
      </div>
      <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">How many extra seats?</label>
      <input
        type="number" min={1} max={500} value={qty}
        onChange={(e) => setQty(e.target.value)}
        data-testid="seats-qty-input"
        className="mt-1 w-full border border-neutral-200 focus:border-[#22D3EE] rounded-md px-3 py-2.5 text-sm outline-none"
      />
      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="text-neutral-500">Total</span>
        <strong data-testid="seats-total">
          {seatPrice === null ? "…" : `$${(Math.max(1, Number(qty) || 0) * seatPrice).toFixed(2)}`}
          {seatPrice !== null && <span className="text-[10px] font-normal text-neutral-400 ml-1">(${seatPrice.toFixed(2)}/seat)</span>}
        </strong>
      </div>
      <button onClick={buy} disabled={busy}
        data-testid="seats-buy-btn"
        className="mt-4 w-full bg-[#0A0C10] text-white font-bold text-sm rounded-md py-2.5 hover:bg-black disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Buy {qty || 0} seat{Number(qty) === 1 ? "" : "s"}
      </button>
      <p className="text-[10px] text-neutral-400 mt-3">Secure payment via Stripe. Seats are added as soon as payment completes.</p>
    </Shell>
  );
}
